import {
  AuthToken,
  AuthTokenPayload,
  AuthUser,
} from '@aibees/shared/definition/auth';
import { decode } from 'jsonwebtoken';
import { merge } from 'lodash-es';
import type { Nil, Nullable } from '../definition';
import {
  localStorageGet,
  localStorageRemove,
  localStorageSet,
} from '../utils/storage';
import { AUTH_CACHE_KEY, AUTH_LOGIN_URL, AUTH_URL } from './constants';

export function readUserInfoFromJwt(token: string): Nullable<AuthUser> {
  const data = decode(token);

  if (data && typeof data === 'object') {
    const payload = data as AuthTokenPayload;

    return {
      id: payload.sub,
      firstName: payload.given_name,
      lastName: payload.family_name,
      email: payload.email,
      username: payload.preferred_username || payload.email,
      attributes: {
        alternateEmail: payload.alternateEmail,
        site: payload.site,
        industry: payload.industry,
        company: payload.company,
        avatar: payload.avatar,
        title: payload.title,
        phone: payload.phone,
      },
      roles: payload.resource_access?.web?.roles || [],
      realmAccessRoles: payload.realm_access?.roles || [],
    };
  }

  return null;
}

export function readTokenFromStorage() {
  return localStorageGet<AuthToken>(AUTH_CACHE_KEY);
}

export function saveTokenToStorage(token: AuthToken) {
  return localStorageSet(AUTH_CACHE_KEY, token);
}

export function logoutUser(
  token?: Nil<AuthToken>,
  noRedirect: boolean = false,
  extraKeysToRemove?: string[]
) {
  localStorageRemove(AUTH_CACHE_KEY);
  extraKeysToRemove?.forEach((key) => localStorageRemove(key));
  if (!noRedirect) window.location.href = getLogoutUrl(token);
}

export function getLogoutUrl(token?: Nil<AuthToken>) {
  if (!token) return '#';

  const redirect_uri = encodeURIComponent(AUTH_LOGIN_URL);
  return token?.id_token
    ? `${AUTH_URL}/logout?id_token_hint=${token?.id_token}&post_logout_redirect_uri=${redirect_uri}`
    : AUTH_LOGIN_URL;
}

export const mergeUserData = (
  user?: Nullable<Partial<AuthUser>>,
  ...partials: Nullable<Partial<AuthUser>>[]
) => {
  return partials
    .filter(Boolean)
    .reduce((r, c) => merge(r, c), user || {}) as AuthUser;
};

// CU-86e17r1cr: attempt-id helpers below (start / get / clear) and the two
// sessionStorage keys exist solely to correlate the temporary sign-in telemetry
// events. Delete the whole block when the investigation closes.
const AUTH_ATTEMPT_ID_KEY = '_auth_attempt_id';
const AUTH_ATTEMPT_START_KEY = '_auth_attempt_start';

// crypto.randomUUID() requires a secure context (HTTPS or localhost). Production
// and `nx serve` (localhost) both qualify; this would throw on a non-localhost http origin.
export function startAuthAttempt(): string {
  const id = crypto.randomUUID();
  sessionStorage.setItem(AUTH_ATTEMPT_ID_KEY, id);
  sessionStorage.setItem(AUTH_ATTEMPT_START_KEY, String(Date.now()));
  return id;
}

export function getAuthAttemptContext() {
  const attemptId = sessionStorage.getItem(AUTH_ATTEMPT_ID_KEY) ?? undefined;
  const start = Number(sessionStorage.getItem(AUTH_ATTEMPT_START_KEY)) || 0;
  const elapsedMs = start ? Date.now() - start : undefined;
  return { attemptId, elapsedMs };
}

export function clearAuthAttempt() {
  sessionStorage.removeItem(AUTH_ATTEMPT_ID_KEY);
  sessionStorage.removeItem(AUTH_ATTEMPT_START_KEY);
}
